import { useCallback, useEffect, useState } from "react";
import { Button, Container } from "react-bootstrap";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { type Case, Role } from "@/briefpoint-client";
import AddEditCaseModal from "@/components/CaseManagement/AddEditCaseModal";
import AddEditClientModal from "@/components/CaseManagement/AddEditClientModal";
import Loading from "@/components/Loading";
import SearchInput from "@/components/SearchInput/SearchInput";
import { useCaseApi, useClientApi, useTagApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import useFirm from "@/hooks/useFirm";
import { getCaseSourceLogo } from "@/utils/ExternalPartner/utils";
import "./styles.scss";
import { TrashFill } from "react-bootstrap-icons";
import CaseTableMulti from "@/components/CaseManagement/CaseTableMulti";
import { Modal } from "@/components/Modals";
import PageHeader from "@/components/PageHeader";

import type { CaseResponse, ClientResponse, UserResponse } from "@/lib/types";

type ClientParams = {
  id: string;
};

export default function ClientPageMulti() {
  const { user, firm } = useAuth();
  if (!user) throw new Error("User is undefined");
  if (!firm) throw new Error("Firm is undefined");
  const location = useLocation<{ client: ClientResponse }>();
  const history = useHistory();
  const { id } = useParams<ClientParams>();
  const clientApi = useClientApi();
  const caseApi = useCaseApi();
  const tagApi = useTagApi();
  const [users, , , ,] = useFirm(firm?.id, false);
  const [showAddCase, setShowAddCase] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [cases, setCases] = useState<CaseResponse[] | undefined>();
  const [client, setClient] = useState<ClientResponse | undefined>(
    location.state?.client,
  );
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState<Map<number, string> | undefined>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { jurisdictions } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setShowEditClient(params.get("editClient") === "true");
  }, [location.search]);

  const loadClient = useCallback(
    async (
      user: UserResponse | undefined,
      client: ClientResponse | undefined,
    ) => {
      if (user?.firmId && !client) {
        const client = (await clientApi.clientGet({
          firmId: user.firmId,
          id,
        })) as ClientResponse;
        setClient(client);
      }
    },
    [clientApi, id],
  );

  useEffect(() => {
    loadClient(user, client);
  }, [user, loadClient, client]);

  const loadCases = useCallback(
    async (user: UserResponse | undefined) => {
      if (user?.firmId && id) {
        const cases = (await caseApi.caseGetAll({
          firmId: user.firmId,
          clientId: id,
          usersCasesOnly: !user?.role?.includes(Role.FirmAdmin),
        })) as CaseResponse[];
        setCases(cases);
      }
    },
    [caseApi, id],
  );

  useEffect(() => {
    loadCases(user);
  }, [user, loadCases]);

  useEffect(() => {
    async function loadTags() {
      const tagsList = new Map<number, string>();

      const loadedTags = await tagApi.tagFindTags({});

      for (const t of loadedTags) {
        tagsList.set(t.id, t.name);
      }
      setTags(tagsList);
    }
    if (!tags) {
      loadTags();
    }
  }, [tagApi, tags]);

  async function AddCase(_case: Case) {
    const newCase = (await caseApi.casePost({
      firmId: user?.firmId || "",
      clientId: id,
      _case,
    })) as CaseResponse;

    if (newCase) {
      if (cases) {
        setCases((c) => [...(c ?? []), newCase]);
      } else {
        setCases([newCase]);
      }
    }

    closeAddCase();
  }

  function closeAddCase() {
    setShowAddCase(false);
  }

  async function handleClientDelete() {
    await clientApi.clientDelete({
      firmId: user?.firmId || "",
      id: client?.id ?? "",
    });

    history.push("/clients");
  }

  const filteredCases = cases?.filter((x) => {
    if (!search) return true;

    const split = (x.title || "").concat(x.shortTitle || "").split(" ");

    for (let i = 0; i < split?.length; i++) {
      if (split[i].toLowerCase().startsWith(search.toLowerCase())) {
        return true;
      }
    }

    return false;
  });

  return (
    <>
      <Container className="page-container-multi">
        <Loading
          isLoading={
            client === undefined &&
            cases === undefined &&
            jurisdictions === undefined &&
            users === undefined
          }
          className="mt-3"
        >
          <PageHeader
            title={
              <div className="bread-crumbs">
                <Link to={"/clients"}>Clients</Link> {`> ${client?.name}`}
              </div>
            }
            className="mt-0"
          />

          <div className="client-details-content">
            <div className="client-details-section reduced-padding">
              <div className="client-name-header">
                <p>
                  <span style={{ width: "18px", display: "inline-block" }}>
                    {getCaseSourceLogo(client?.integration?.partner)}
                  </span>{" "}
                  {client?.name}
                </p>
              </div>
              <div className="d-flex justify-content-between gap-2 align-items-center">
                <div className="d-flex gap-2 align-items-center">
                  <Button
                    variant="outline-secondary fs-6 btn-hover-bg-fix"
                    onClick={() => setShowEditClient(true)}
                  >
                    Edit Client Details
                  </Button>
                  {cases && !cases.length && (
                    <TrashFill
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowDeleteConfirm(true)}
                      title="Delete Client"
                    />
                  )}
                </div>
                <Button variant="primary" onClick={() => setShowAddCase(true)}>
                  Add New Case
                </Button>
              </div>
            </div>
            <div className="client-details-section reduced-padding">
              <div className="client-search-wrapper">
                <SearchInput
                  value={search}
                  placeholder="Search cases"
                  onChange={(value) => setSearch(value.toLowerCase())}
                  className="mb-2"
                />
              </div>
            </div>
            <div className="client-details-section">
              {(filteredCases?.length ?? 0) > 5 && (
                <div>
                  <CaseTableMulti
                    cases={filteredCases
                      ?.slice()
                      .sort((a, b) => {
                        if (a.lastActivity && b.lastActivity) {
                          return a.lastActivity > b.lastActivity ? -1 : 1;
                        }
                        if (a.lastActivity) {
                          return -1;
                        }
                        if (b.lastActivity) {
                          return 1;
                        }
                        return 0;
                      })
                      .slice(0, 5)}
                    addCase={() => setShowAddCase(true)}
                    users={users}
                    jurisdictions={jurisdictions}
                    showIfEmpty={false}
                  />
                  <span>&nbsp;&nbsp;</span>
                  <h2 style={{ margin: "0px", marginBottom: "8px" }}>
                    All Cases
                  </h2>
                </div>
              )}
              <CaseTableMulti
                cases={filteredCases?.slice().sort((a, b) => {
                  if (a.shortTitle && b.shortTitle) {
                    return a.shortTitle.localeCompare(b.shortTitle);
                  }
                  if (a.shortTitle) {
                    return -1;
                  }
                  if (b.shortTitle) {
                    return 1;
                  }
                  return 0;
                })}
                addCase={() => setShowAddCase(true)}
                users={users}
                jurisdictions={jurisdictions}
                showIfEmpty={true}
              />
            </div>
          </div>
          {client && firm && (
            <>
              <AddEditClientModal
                title="Edit Client"
                submitButtonText="Save"
                isShow={showEditClient}
                onClose={() => setShowEditClient(false)}
                onClientSaved={setClient}
                client={client}
                users={users}
              />
              <AddEditCaseModal
                availableTags={tags}
                setAvailableTags={setTags}
                title="Add Case"
                confirm="Add"
                isShow={showAddCase}
                onClose={() => setShowAddCase(false)}
                onConfirm={AddCase}
                users={users}
                jurisdictions={jurisdictions ?? []}
                client={client}
              />
            </>
          )}
        </Loading>
      </Container>
      <Modal
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleClientDelete}
        show={showDeleteConfirm}
        confirmText="Yes, Delete"
        title="Delete Client"
      >
        Are you sure you want to delete this Client? This action cannot be
        undone.
      </Modal>
    </>
  );
}
